import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Download } from "lucide-react";
import PicImg from "../PicImg";

/**
 * Premium Product Hero section — identical across every LIFTILE product page.
 *
 * Props:
 *  - testId: string (e.g., "lpmh-hero")
 *  - backgroundImage: string (public path to hero photograph)
 *  - eyebrow: string  (e.g., "LPMH Series")
 *  - title: ReactNode (JSX allowed for line breaks + orange spans)
 *  - subtitle: string
 *  - models?: string[]  (e.g., ["LPMH1000", "LPMH1500", "LPMH2000"]) — optional chip strip
 *  - primaryCta: { label, href }
 *  - secondaryCta?: { label, onClick? }
 *  - badges: string[]
 *  - specs: { Icon, value, label }[]
 */
export default function ProductHero({
  testId,
  backgroundImage,
  eyebrow,
  title,
  subtitle,
  models,
  primaryCta,
  secondaryCta,
  badges = [],
  specs = [],
  titleSizeClass = "text-4xl sm:text-5xl md:text-6xl lg:text-[3.75rem] xl:text-[4.25rem]",
  overlayGradient = "bg-gradient-to-r from-liftile-black/95 via-liftile-black/78 to-liftile-black/40",
}) {
  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-black overflow-hidden min-h-[92vh] flex items-center py-16 md:py-24"
    >
      <div className="absolute inset-0">
        <PicImg
          src={backgroundImage}
          alt={eyebrow}
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 ${overlayGradient}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-liftile-black/85 via-transparent to-liftile-black/60" />
        <div className="absolute inset-0 industrial-grid opacity-15" />
      </div>

      <div className="container-x relative w-full grid lg:grid-cols-[1.15fr_1fr] gap-12 xl:gap-16 items-center">
        {/* LEFT — Copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="heading-eyebrow mb-5"
          >
            {eyebrow}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.75 }}
            data-testid={`${testId}-heading`}
            className={`font-display uppercase text-liftile-cream leading-[1.02] tracking-tight ${titleSizeClass}`}
          >
            {title}
          </motion.h1>

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.75 }}
              className="font-body text-liftile-cream/75 mt-7 max-w-xl text-base md:text-lg leading-relaxed"
            >
              {subtitle}
            </motion.p>
          )}

          {models && models.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.75 }}
              data-testid={`${testId}-models`}
              className="mt-7 flex flex-wrap items-center gap-2.5"
            >
              <span className="font-heading text-[10px] uppercase tracking-[0.24em] text-liftile-cream/50 font-semibold mr-1">
                Models
              </span>
              {models.map((m) => (
                <span
                  key={m}
                  className="inline-block bg-white/[0.06] border border-white/[0.14] text-liftile-cream/90 font-heading text-[11px] uppercase tracking-[0.18em] font-semibold px-3 py-1.5 rounded-full"
                >
                  {m}
                </span>
              ))}
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.75 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            {primaryCta && (
              <Link
                to={primaryCta.href}
                data-testid={`${testId}-quote-btn`}
                className="group inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-[15px] rounded-md hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-12px_rgba(255,107,26,0.85)] transition-all duration-400"
              >
                {primaryCta.label}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            )}
            {secondaryCta && (
              <a
                href="/liftile-brochure.pdf"
                download="LIFTILE-Product-Catalogue.pdf"
                onClick={secondaryCta.onClick}
                data-testid={`${testId}-brochure-btn`}
                className="inline-flex items-center justify-center gap-3 border border-liftile-cream/30 text-liftile-cream font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-[15px] rounded-md hover:bg-liftile-cream hover:text-liftile-matte-black transition-all duration-300"
              >
                {secondaryCta.label}
                <Download className="w-4 h-4" />
              </a>
            )}
          </motion.div>

          {badges.length > 0 && (
            <motion.ul
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.75 }}
              data-testid={`${testId}-badges`}
              className="mt-10 flex flex-wrap gap-x-6 gap-y-2.5"
            >
              {badges.map((b) => (
                <li
                  key={b}
                  className="inline-flex items-center gap-2 font-heading text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-liftile-cream/80 font-semibold"
                >
                  <Check
                    className="w-3.5 h-3.5 text-liftile-orange"
                    strokeWidth={3}
                  />
                  {b}
                </li>
              ))}
            </motion.ul>
          )}
        </motion.div>

        {/* RIGHT — Floating spec cards */}
        {specs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.85 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6"
          >
            {specs.map((s, i) => (
              <motion.div
                key={s.label}
                data-testid={`${testId}-spec-${i}`}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.45 + i * 0.1,
                  duration: 0.65,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -4 }}
                className="group relative rounded-2xl bg-white/[0.06] backdrop-blur-xl border border-white/[0.14] p-6 md:p-7 transition-all duration-500 hover:border-liftile-orange/50 hover:bg-white/[0.09] hover:shadow-[0_20px_45px_-12px_rgba(255,107,26,0.35)]"
              >
                <div
                  aria-hidden
                  className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(120% 80% at 100% 0%, rgba(255,107,26,0.18) 0%, transparent 60%)",
                  }}
                />
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-liftile-orange/15 flex items-center justify-center mb-5">
                    <s.Icon
                      className="w-5 h-5 text-liftile-orange"
                      strokeWidth={1.6}
                    />
                  </div>
                  <div className="font-display text-2xl md:text-[1.75rem] text-liftile-cream leading-tight">
                    {s.value}
                  </div>
                  <div className="font-heading text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-liftile-cream/60 mt-2 font-semibold">
                    {s.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}
